import React, { useState, useEffect, useContext, } from 'react'
import { Link } from 'react-router-dom';
import { Table, Button, Space, Row, Typography, Col, Input, Tag, Dropdown, Skeleton, Spin, } from 'antd';
import { DeleteOutlined, SearchOutlined, UserAddOutlined, UserOutlined, MoreOutlined, EditOutlined, SyncOutlined, } from '@ant-design/icons';
import { AccessManagementDeleteUserModal } from '../Modals/AccessManagementDeleteUserModal.component';
import { ActivateUserModal } from '../Modals/ActivateUserModal.component';
import { Restricted } from '../../UI/Restricted.component';
import { Modal } from '../../UI/Modal/Modal.component';
import { LoadingModal } from '../Modals/LoadingModal.component';
import { PermissionsContext } from '../../../router/DashboardRoutes';
import { AddNewUserForm } from './AddNewUserForm.component';
import { requestUsers } from '../../../services/UserService';
import { USER_TYPE, PERMISSION_TYPE } from '../../../Constants';

const { Text, Title, } = Typography;

export const AccessManagementTable = () => {

    const userPermissions = useContext(PermissionsContext);

    const [search, setSearch] = useState('');
    const [filters, setFilters] = useState({});

    const [locations, setLocations] = useState([]);
    const [filteredResults, setFilteredResults] = useState('');
    const [currentRow, setCurrentRow] = useState('');
    const [deleteUserModal, setDeleteUserModal] = useState(false);
    const [addTechUserModal, setAddTechUserModal] = useState(false);
    const [displayActivateUserModal, setDisplayActivateUserModal] = useState(false);

    const [dataSource, setDataSource] = useState([]);
    const [refreshData, setRefreshData] = useState();

    const { ADMIN, TECH, CUSTOMER } = USER_TYPE;

    useEffect(() => {
        setRefreshData(true);
    }, [])

    useEffect(() => {
        let loadingTimeout;
        let dataFetched = false;
        let minLoadingTime = 1500;

        if(refreshData){
            loadingTimeout = setTimeout(() => {
                if(dataFetched){
                    setRefreshData(false);
                    setCurrentRow('');
                }
            }, minLoadingTime);

            requestUsers().then(function(response) {
                if(response){
                    dataFetched = true;
                    setDataSource(response);
                    if(loadingTimeout){
                        clearTimeout(loadingTimeout); // clears the minimum timeout
                        setTimeout(() => {
                            setRefreshData(false);
                        }, minLoadingTime);
                    }
                }
            })
        }

        return () => {
            // clean timeout on component unmount
            clearTimeout(loadingTimeout); 
        }
    }, [refreshData])

    useEffect(() => {
        createLocationsList(dataSource);
    }, [dataSource])

    const createLocationsList = (data) => {
        data?.forEach(location => {
            if( location.userType === "U" ) {
                setLocations(locations => [...locations, { 'id': location.id, 'companyName': location.companyName, 'username': location.username }]);
            }
        });
    }

    const handleEnter = (e) => {
        if (e.key === 'Enter') {
            return false;
        }
    }

    const getUserType = (userType) => {
        switch (userType) {
            case CUSTOMER:
                return 'Customer';
            case TECH:
                return 'Tech';
            case ADMIN:
                return 'Admin';
            default:
                return '';
        }
    }
    
    const columns = [
        {
            title: 'Username',
            dataIndex: 'username',
            width: 400,
            sorter: (a, b) => a.username.length - b.username.length,
        },
        {
            title: 'Role',
            dataIndex: 'userType',
            filters: [
                {
                    text: 'Customer', 
                    value: CUSTOMER
                }, 
                {
                    text: 'Tech', 
                    value: TECH
                }
            ], 
            onFilter: (value, record) => record.userType.includes(value),
            filteredValue: filters.userType || null,
            render: (value) => {
                return getUserType(value);
            },
        },
        {
            title: 'Privileges',
            dataIndex: 'Accesses',
            filters: [
                {
                    text: 'Read', 
                    value: PERMISSION_TYPE.READ,
                }, 
                {
                    text: 'Read/Write', 
                    value: PERMISSION_TYPE.WRITE,
                }
            ], 
            onFilter: (value, record) => record.Accesses[0]?.accessType === value,
            filteredValue: filters.Accesses || null,
            render: (value, record) => {
                return value[0]?.accessType === 'W' ? 'Read/Write' : 'Read'
            },
        },
        {
            title: 'Status', 
            dataIndex: 'accessDisabled',
            filters: [
                {
                    text: 'Active', 
                    value: false,
                }, 
                {
                    text: 'Inactive', 
                    value: true
                }
            ], 
            filteredValue: filters.accessDisabled || null,
            onFilter: (value, record) => record.accessDisabled === value, 
            render: (value) => {
                const renderUserStatus = (value) => {
                    if (value) {
                        return <Tag style={{borderRadius: 10}} color='default'>Inactive</Tag>
                    } 
                        return <Tag style={{borderRadius: 10}} color={'green'}>Active</Tag>
                }

                return (
                    <>
                        <Space>
                            { renderUserStatus(value) }
                        </Space>
                    </>
                )
            }
        }, 
        {   
            key: 'id',
            width: 10,
            hidden: !userPermissions.accessManagement.editUser,
            render: (_, record) => {
                let accountType = getUserType(record.userType)

                const items = [
                    {
                        key: '0', 
                        label: <Link to='../edit-user' state={{...record, locations, accountType}}>Edit User</Link>, 
                        icon: <EditOutlined/>
                    },
                    {
                        key: '1',
                        label: 'Activate',
                        icon: <UserOutlined />,
                        disabled: !record.accessDisabled,
                        onClick: () => {
                            setCurrentRow(record); 
                            setDisplayActivateUserModal(true);
                        }
                    },
                    
                    {
                        key: '2',
                        label: <Text type='danger'>Delete</Text>,
                        icon: <DeleteOutlined style={{color: 'var(--error-color)'}}/>,
                        onClick: () => {
                            setCurrentRow(record); 
                            setDeleteUserModal(true);
                        }
                    },
                ]

                return (
                    <>
                        <Restricted to='accessManagement.editUser'>
                            <Space size="small" align='start'>
                                {
                                    <Dropdown.Button
                                        menu={{items}}
                                        size='small'
                                        icon={<MoreOutlined/>}
                                        type='link'
                                        placement='bottomLeft'
                                    />
                                }
                            </Space>
                        </Restricted>
                    </>
                );
            },
        }
    ].filter(item => !item.hidden);;

    const handleTableChange = (pagination, filters, sorter) => {
        setFilters(filters);
    };

    const clearFilters = () => {
        setFilters({});
    };

    useEffect(() => {
        setFilteredResults(dataSource.filter(user => {
            let regEx = new RegExp(search.toLowerCase(), 'g');
            return regEx.test(Object.entries(user).map( function([id, value]) {
                if ( value == null )
                    value = ''
                return ['', value.toString().toLowerCase()]
            }))
        }));
    }, [dataSource, search, filteredResults.length]);

    const showTotalMessage = (total, range) => {
        
        let defaultMessage = `Showing ${range[0]}-${range[1]} of ${total} entries`;
        let filteredMessage = `${defaultMessage} containing '${search}'. Results filtered from ${dataSource.length} entries.`;

        return search ? filteredMessage : defaultMessage;
    }

    const renderFilters = () => {

        const handleRemove = (filterName, indexToRemove) => {
            setFilters((prevFilters) => ({
                ...prevFilters,
                [filterName]: prevFilters[filterName]?.filter((_, index) => index !== indexToRemove),
            }));
        };

        const renderTagEl = (filterType, index) => {
            const filterTagProps = {
                style: {borderRadius: 10},
                closable: true, 
                color: 'geekblue', 
                onClose: () => handleRemove(filterType, index),
            }
            return filterTagProps;
        }

        return (
            <>
                {filters.userType?.map((type, i) => (
                    <Tag {...renderTagEl('userType', i)} key={type}> Role: {getUserType(type)}</Tag>
                ))}

                {filters.Accesses?.map((type, i) => (
                    <Tag {...renderTagEl('Accesses', i)} key={type}>Privilege: {type === PERMISSION_TYPE.WRITE ? 'Read/Write' : 'Read'}</Tag>
                    
                ))}

                {filters.accessDisabled?.map((type, i) => (
                    <Tag {...renderTagEl('accessDisabled', i)} key={type ? 'inactive' : 'active'}> Status: {type ? 'Inactive' : 'Active'}</Tag>
                ))}
            </>
        )
    }

    let hasFilters =  Object.values(filters).some(
        (value) => value !== null && Array.isArray(value) && value.length > 0    
    );


    return (
        <>  
            <Row gutter={[16,16]}>
                <Col span={24}>
                    <Title level={5}>All Users <Text type='secondary'>{(dataSource?.length)}</Text></Title>
                </Col>

                <Col span={24}>
                    <Row justify='space-between'>
                        <Space direction='horizontal' size='small'>
                            <p>Search:</p>
                            <Input suffix={<SearchOutlined/>} placeholder="input search text" allowClear value={search} onChange={(e) => setSearch(e.target.value)} />
                            { search &&
                                <Button type='primary' onKeyDown={(e) => handleEnter(e)} onClick={() => setSearch('')}>Clear</Button> 
                            }
                        </Space>
                        <Space>
                            <Button icon={<SyncOutlined/>} onClick={() => setRefreshData(true)}>Refresh</Button>
                            <Restricted to='accessManagement.addTech'>
                                <Button onClick={() => setAddTechUserModal(true)} type="primary" icon={<UserAddOutlined />}>Add Tech User</Button>
                            </Restricted>
                        </Space>
                    </Row>
                </Col>
                
                { hasFilters && 
                    <Col span={24}>
                        Active Filters: <span/>
                        {renderFilters()}
                        <Button type='link' size='small'  onClick={() => clearFilters()}>Clear All</Button>
                    </Col>
                }

                <Col span={24}>
                    { refreshData ? <Skeleton/> : 
                        <>
                            <Table
                                size="small"
                                columns = {columns}
                                dataSource = {search  !== '' ? filteredResults : dataSource}
                                rowKey = 'id'
                                scroll={{
                                    x: "1000",
                                }}
                                tableLayout="auto"
                                pagination={
                                {
                                    // total: search ? filteredResults.length : dataSource.length,
                                        defaultCurrent: 1,
                                        showTotal: (total, range) =>  showTotalMessage(total, range),
                                        showSizeChanger: true,
                                        showQuickJumper: true
                                    }
                                }
                                onChange={handleTableChange}
                            />
                            { (search && filteredResults.length === 0)  &&
                                <Row justify='end'>
                                    <p style={{marginTop: 30}}>0 matching results containing "{search}".</p> 
                                </Row>
                            }
                        </>
                    }

                    { deleteUserModal && 
                        <AccessManagementDeleteUserModal
                            user={currentRow}
                            displayModal={deleteUserModal} 
                            setDisplayModal={setDeleteUserModal}
                            setRefreshData={setRefreshData}
                        />
                    }

                    { addTechUserModal &&
                        <Modal title='Add Tech User' visible={addTechUserModal} setVisible={setAddTechUserModal} actionGroup={false}>
                            <AddNewUserForm access={dataSource} visible={setAddTechUserModal} setRefreshData={setRefreshData}/>
                        </Modal>
                    }

                    { displayActivateUserModal &&
                        <ActivateUserModal 
                            user={currentRow}
                            displayModal={displayActivateUserModal}
                            setDisplayModal={setDisplayActivateUserModal}
                            refreshData={refreshData}
                            setRefreshData={setRefreshData}
                        />
                    }

                    <Spin spinning={refreshData} fullscreen/>
                </Col>
            </Row>
        </>
    );
}